import { useRef, useState, type DragEvent } from 'react';
import { formatFileSize } from '../format';
import { uploadLabels } from '../fileValidation';
import type { ProviderInfo } from '../types';

/** What a file for this provider has to look like, in plain words. The server still enforces every rule. */
export function FileRequirements({
  maxUploadBytes, provider,
}: { maxUploadBytes: number; provider?: Pick<ProviderInfo, 'id' | 'supportLevel' | 'acceptedFileTypes'> }) {
  const level = provider?.supportLevel ?? 'NativeTemplate';
  const template = provider?.id === 'EpnStandardTemplate';
  return (
    <section aria-labelledby="phi-requirements-heading">
      <h3 id="phi-requirements-heading">File requirements</h3>
      {level === 'NativeTemplate' && !template && (
        <ul>
          <li>A single .zip file, no other file types.</li>
          <li>Maximum size: {formatFileSize(maxUploadBytes)}.</li>
          <li>Not encrypted or password-protected.</li>
          <li>Contains the six CSV data files and manifest.json from the Escochex Payroll export.</li>
        </ul>
      )}
      {template && (
        <ul>
          <li>A single .xlsx workbook filled in from the EPN Standard Payroll History Template.</li>
          <li>Maximum size: {formatFileSize(maxUploadBytes)}.</li>
          <li>Not password-protected, and without formulas, macros or links to other files.</li>
          <li>Keep the template&apos;s sheet names and column headings as they are.</li>
        </ul>
      )}
      {level !== 'NativeTemplate' && (
        <ul>
          <li>A single spreadsheet (.xlsx) or .csv file with one header row that names each column.</li>
          <li>Maximum size: {formatFileSize(maxUploadBytes)}.</li>
          <li>One row per employee per pay run, with plain, non-negative amounts and no formulas.</li>
          <li>Dates written as month/day/year (for example 9/25/2025).</li>
          <li>Not password-protected.</li>
        </ul>
      )}
    </section>
  );
}

/** Drag-and-drop area plus a keyboard-accessible button that opens the native picker. */
export function FileDropzone({
  disabled = false, onFiles, inputLabel, acceptedFileTypes = ['.zip'],
}: { disabled?: boolean; onFiles: (files: FileList | null) => void; inputLabel?: string; acceptedFileTypes?: string[] }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const labels = uploadLabels(acceptedFileTypes);

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (!disabled) onFiles(event.dataTransfer.files);
  };

  const accept = acceptedFileTypes.includes('.zip') ? `${acceptedFileTypes.join(',')},application/zip` : acceptedFileTypes.join(',');

  return (
    <div
      className={`phi-dropzone${dragging ? ' phi-dropzone--active' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <p>{labels.drop}</p>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="phi-sr-only"
        tabIndex={-1}
        aria-label={inputLabel ?? labels.input}
        disabled={disabled}
        onChange={(e) => onFiles(e.target.files)}
      />
      <button type="button" className="phi-btn-secondary" disabled={disabled} onClick={() => inputRef.current?.click()}>
        {labels.button}
      </button>
    </div>
  );
}
